/**
 * Toucan Reads — pulling down what has not been read yet, so it can be read
 * with no network, and saying which of the pile is not down yet.
 *
 * A device has only what it has fetched. That means an item's content (see
 * `itemContent`) and the pictures in a reading, which the worker keeps once
 * something asks for them. This asks for both before they are wanted,
 * whenever the library is on screen.
 *
 * Only what is still to be read: the archive is dealt with, and pulling it
 * down would put a whole library on a phone.
 */

import { useEffect, useState } from "preact/hooks";
import {
  blobHeld,
  blobText,
  canHoldOffline,
  ensureBlob,
  fetchPage,
  heldOffline,
  holdPicture,
  isKeptDocument,
  keepDocument,
  required,
} from "toucan";
import {
  isArchived,
  isGone,
  isSettled,
  isWebAddress,
  itemContent,
  setItemMissing,
  worthAsking,
} from "./model.ts";
import { store } from "./state.ts";
import type { Item, ItemContent, ReadsStore, Waiting } from "./model.ts";

/**
 * Everything still to be read that this device could hold, in the order the
 * items are in — so the oldest saves, the ones furthest down the pile, are not
 * the last to arrive.
 *
 * A device with nowhere to put a file leaves papers out of it: there is
 * nothing to fetch that would still be there afterwards, and nothing to say
 * about it on a card either.
 */
export function unreadContent(items: ReadsStore["items"]): Item[] {
  return items.filter((item) => {
    const content = itemContent(item);
    if (isArchived(item) || content === undefined) {
      return false;
    }
    return content.kind === "text" || canHoldOffline();
  });
}

/**
 * The addresses a `srcset` offers, each entry's address without the width or
 * density that follows it.
 */
function offered(srcset: string): string[] {
  return srcset.split(",").flatMap((entry) => entry.trim().split(/\s+/, 1));
}

/**
 * The pictures a reading draws, which are the one above it and every one
 * inside it.
 *
 * Every address a `srcset` offers, not the one this screen would pick: which
 * candidate a browser asks for depends on the window, and a phone turned
 * sideways offline would otherwise find the wrong ones held.
 *
 * They come in drawing order with each address once. The pile is walked one
 * picture at a time, so the top of the reading arrives first.
 */
export function picturesIn(item: Item, reading: string | undefined): string[] {
  const drawn: string[] = [];
  if (item.thumbUrl !== undefined) {
    drawn.push(item.thumbUrl);
  }
  if (reading !== undefined) {
    const page = new DOMParser().parseFromString(reading, "text/html");
    for (const picture of page.querySelectorAll("img")) {
      const src = picture.getAttribute("src");
      if (src !== null) {
        drawn.push(src);
      }
      const srcset = picture.getAttribute("srcset");
      if (srcset !== null) {
        drawn.push(...offered(srcset));
      }
    }
  }
  // A picture written into the page as `data:` is already in the reading's
  // own bytes, and a relative address in an extracted reading resolves against
  // this app rather than the site it came from — neither is a thing to fetch.
  const wanted: string[] = [];
  for (const address of drawn) {
    if (isWebAddress(address) && !wanted.includes(address)) {
      wanted.push(address);
    }
  }
  return wanted;
}

/**
 * Ask for the pictures an item's reading draws, so it shows them offline.
 *
 * Best effort, and no part of what a card says: a missing picture is a gap in
 * a reading rather than a reading nobody can open, and an advert's tracking
 * pixel that 404s should not mark an article as missing.
 *
 * Only a reading made of text has any, since a paper carries its pictures
 * inside the file. A device with nowhere to put a picture asks for none.
 */
async function holdPictures(item: Item, content: ItemContent): Promise<void> {
  if (content.kind !== "text" || !canHoldOffline()) {
    return;
  }
  for (const address of picturesIn(item, await blobText(content.hash))) {
    if (!(await heldOffline(address))) {
      await holdPicture(address);
    }
  }
}

/** Whether this device already holds what an item is made of. */
export async function contentHeld(content: ItemContent): Promise<boolean> {
  return content.kind === "text"
    ? await blobHeld(content.hash)
    : await heldOffline(content.url);
}

/**
 * What asking for an item's copy came to. `refused` is the site's answer about
 * the item, which its card shows. `later` is anything else that stopped it,
 * such as a site too busy to answer or a device offline. That is no fault of
 * the item's, so its card goes on saying the copy is coming, with no toast.
 */
type Asked =
  | { outcome: "held" }
  | { outcome: "refused"; status: number }
  | { outcome: "later" };

/**
 * Put what an item is made of on the device, and say what stopped it where
 * something did.
 *
 * A paper goes through the proxy, since plenty of hosts refuse a script, and
 * the worker caches it on the way past. Where the worker did not, the page
 * keeps the copy it has just been given.
 */
async function holdContent(content: ItemContent): Promise<Asked> {
  if (content.kind === "text") {
    await ensureBlob(content.hash);
  } else {
    let page;
    try {
      page = await fetchPage(content.url);
    } catch {
      return { outcome: "later" };
    }
    if (page.status >= 400) {
      return isSettled(page.status)
        ? { outcome: "refused", status: page.status }
        : { outcome: "later" };
    }
    if (
      isKeptDocument(page.contentType, content.url) &&
      !(await contentHeld(content))
    ) {
      await keepDocument(content.url, page.bytes, page.contentType);
    }
  }
  return (await contentHeld(content))
    ? { outcome: "held" }
    : { outcome: "later" };
}

/**
 * What this device holds of the library, and whether that has been found out
 * yet.
 *
 * "Held nothing" and "not asked yet" look the same in a set, and without
 * `checked` every card would show a missing mark before the first read and
 * then drop it.
 */
export interface HeldContent {
  /** The items whose content is on this device. */
  ids: ReadonlySet<string>;
  /** Whether the read that says so has finished at least once. */
  checked: boolean;
}

/**
 * Whether there is anything about this item a device could ever keep.
 *
 * A link's reading is the live site, so there is never anything to fetch, and
 * the same goes for a paper on a device with nowhere to put a file. An article
 * says yes even before its text is extracted, since "not yet" differs from
 * "not ever".
 *
 * A save nobody has looked up yet counts as coming, or everything saved
 * offline would show "never" until the sweep reached it.
 */
export function holdable(item: Item): boolean {
  if (item.kind === "pdf") {
    return canHoldOffline();
  }
  return item.kind === "article" || item.fetchedAt === undefined;
}

/**
 * What a card has to say about reading this one with no network. It says
 * nothing if its content is on the device or the item is dealt with, and
 * nothing before the first read of what the device holds (see `HeldContent`).
 * A failure shows whatever the device holds, since a paper that will not read
 * can still be on the device.
 */
export function waitingFor(item: Item, held: HeldContent): Waiting | undefined {
  // A paywalled card's own mark already says its reading is somewhere else.
  if (isArchived(item) || item.paywalled === true) {
    return undefined;
  }
  if (item.failed !== undefined) {
    return "failed";
  }
  if (!held.checked || held.ids.has(item.id)) {
    return undefined;
  }
  if (item.missing !== undefined) {
    return isGone(item.missing) ? "gone" : "refused";
  }
  return holdable(item) ? "coming" : "never";
}

/**
 * Fetch what this device does not hold, one item at a time, while `running` —
 * and hand back the ids of the items it does hold, so a card can say which of
 * them would open with no network.
 *
 * One at a time, because this is work nobody asked for, competing with the
 * sweep's fetches over the same connection. A fully held library costs one
 * read per item and stops.
 *
 * What is held is read in full before anything is fetched, so a held card
 * does not flash "not on this device" while the cards above it are asked
 * about.
 */
export function usePrecache(running: boolean): HeldContent {
  const [held, setHeld] = useState<HeldContent>({
    ids: new Set(),
    checked: false,
  });
  const wanted = running ? unreadContent(store().items) : [];
  // What there is to hold, as a key: a save or a sync that adds to the pile
  // starts the walk again, and nothing else does.
  const key = wanted.map((item) => item.id).join(",");
  useEffect(() => {
    // Leaving the library asks for no pass and disturbs nothing: what was
    // found last time is still true of the device, and throwing it away would
    // put every card back to "not held" for as long as the next read takes.
    if (!running) {
      return;
    }
    const pull = async (): Promise<void> => {
      const found = new Set<string>();
      const missing: Item[] = [];
      for (const item of wanted) {
        const content = itemContent(item);
        if (content !== undefined && (await contentHeld(content))) {
          found.add(item.id);
        } else {
          missing.push(item);
        }
      }
      setHeld({ ids: new Set(found), checked: true });
      const now = Date.now();
      for (const item of missing) {
        if (!worthAsking(item, now)) {
          continue;
        }
        // Everything walked here is something to hold: `unreadContent` leaves
        // out anything this device could keep nothing of.
        const asked = await holdContent(
          required(itemContent(item), "the item's content"),
        );
        if (asked.outcome === "held") {
          setItemMissing(item, undefined, now);
          found.add(item.id);
          // A copy per item, because the cards are drawn from the set. Preact
          // sees a set mutated in place as the same object, so nothing would
          // redraw until the pass ended.
          setHeld({ ids: new Set(found), checked: true });
        } else if (asked.outcome === "refused") {
          setItemMissing(item, asked.status, now);
        }
      }
      // The pictures come last, once every reading that can be here is here:
      // the words are what the pile is for, and a picture is what makes one of
      // them look right. A reading this device could not get has none to ask
      // for — the addresses are inside it.
      for (const item of wanted) {
        const content = itemContent(item);
        if (content !== undefined && found.has(item.id)) {
          await holdPictures(item, content);
        }
      }
    };
    // Nothing waits for this: what a pass leaves behind is bytes on the
    // device, so a pass in flight when the library is left runs to its end.
    void pull();
    // `wanted` is rebuilt on every render; what it holds is what decides
    // whether there is a pass to make.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, running]);
  return held;
}
